/* Emirates Tracking Update — upload the courier workbook (one sheet per
 * dispatch date), preview smart matches, apply to Order Register + Database A. */
import { useState, useRef, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, Loader2, PlaneTakeoff, Search, Undo2, Upload, AlertTriangle, Check, X } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { API, getAuthHeader } from "@/App";
import { AwaitingTrackingBadge } from "./AwaitingTracking";

const STATUS = {
  auto: { label: "Auto", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  review: { label: "Review", cls: "bg-amber-100 text-amber-800 border-amber-300" },
  already: { label: "Already set", cls: "bg-indigo-100 text-indigo-700 border-indigo-200" },
  conflict: { label: "Conflict", cls: "bg-rose-100 text-rose-700 border-rose-300" },
  notfound: { label: "Not found", cls: "bg-slate-100 text-slate-600 border-slate-300" },
};
const isTemp = (t) => !t || /^EMI\d{5,7}$/i.test(t) || !isNaN(Number(t));
const ZIP_ICON = { match: <Check className="w-3 h-3 text-emerald-600" />, mismatch: <X className="w-3 h-3 text-rose-600" />,
  unknown: <span className="text-slate-400">?</span> };

const initialSel = (rows) => Object.fromEntries(rows.map(r => [r.row, {
  cid: r.pick || "", on: r.status === "auto", replace: false }]));

const Recent = ({ onUndone }) => {
  const [list, setList] = useState([]);
  const load = () => axios.get(`${API}/order-register/emirates-tracking/batches`, { headers: getAuthHeader() })
    .then(r => setList(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const undo = async (b) => {
    if (!window.confirm(`Undo upload "${b.filename}"? Trackings set by it go back to their old values.`)) return;
    try {
      const r = await axios.post(`${API}/order-register/emirates-tracking/batches/${b.id}/undo`, {}, { headers: getAuthHeader() });
      toast.success(`Undone — ${r.data.or_rows} order rows, ${r.data.dba_rows} Database A rows restored`);
      load(); onUndone?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Undo failed"); }
  };
  if (!list.length) return null;
  return (
    <div className="mt-4" data-testid="emi-recent-uploads">
      <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1">Recent uploads</p>
      <div className="space-y-1 max-h-48 overflow-y-auto">
        {list.map(b => (
          <div key={b.id} className="flex items-center gap-2 text-xs border rounded px-2 py-1 bg-white" data-testid={`emi-batch-${b.id}`}>
            <span className="font-medium truncate max-w-[180px]">{b.filename}</span>
            <span className="text-slate-500">{new Date(b.created_at + (String(b.created_at).endsWith("Z") ? "" : "Z")).toLocaleString()}</span>
            <span className="text-slate-500">· {b.counts?.applied} parcels · {b.user_name}</span>
            {b.undone ? <span className="ml-auto text-[10px] text-slate-400">undone</span> : (
              <Button size="sm" variant="ghost" className="ml-auto h-6 px-2 text-[11px] text-rose-600"
                      onClick={() => undo(b)} data-testid={`emi-batch-undo-${b.id}`}>
                <Undo2 className="w-3 h-3 mr-1" />Undo
              </Button>)}
          </div>
        ))}
      </div>
    </div>
  );
};

const MatchSelect = ({ r, cands, value, onChange }) => {
  const opts = [...r.candidates.map(m => ({ ...m, old: false })), ...(r.suggestions || []).map(m => ({ ...m, old: true }))];
  if (!opts.length) return <span className="text-slate-400 text-[11px]">—</span>;
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)}
            className="h-7 max-w-[260px] text-[11px] border rounded bg-white px-1"
            data-testid={`emi-match-select-${r.row}`}>
      <option value="">— no match —</option>
      {opts.map(m => {
        const c = cands[m.cid] || {};
        return (
          <option key={m.cid} value={m.cid}>
            {c.customer} · {c.date}{m.days != null ? ` (+${m.days}d)` : ""} · {c.code || c.source}
            {m.zip === "match" ? " · zip ✓" : m.zip === "mismatch" ? " · zip ✗" : ""}{m.old ? " · >15 days" : ""}
            {c.tracking ? ` · has ${c.tracking}` : ""}
          </option>
        );
      })}
    </select>
  );
};

const Row = ({ r, cands, s, set }) => {
  const m = [...r.candidates, ...(r.suggestions || [])].find(x => x.cid === s.cid);
  const c = cands[s.cid] || {};
  const needsReplace = !!s.cid && !!c.tracking && !isTemp(c.tracking) && c.tracking !== r.tracking;
  const locked = r.status === "already" || r.status === "conflict";
  return (
    <tr className={`border-b text-[11px] ${s.on ? "bg-emerald-50/40" : ""}`} data-testid={`emi-row-${r.row}`}>
      <td className="px-2 py-1">
        <input type="checkbox" checked={s.on} disabled={locked || !s.cid}
               onChange={(e) => set({ on: e.target.checked })} data-testid={`emi-row-check-${r.row}`} />
      </td>
      <td className="px-2 py-1"><span className={`border rounded-full px-1.5 py-px text-[10px] font-semibold ${STATUS[r.status].cls}`}>{STATUS[r.status].label}</span></td>
      <td className="px-2 py-1 whitespace-nowrap text-slate-500">{r.sheet_date || r.sheet}</td>
      <td className="px-2 py-1 font-medium">{r.name}</td>
      <td className="px-2 py-1 font-mono whitespace-nowrap">
        {r.tracking}{!r.checksum_ok && <AlertTriangle className="inline w-3 h-3 ml-1 text-rose-500" title="Tracking checksum failed — check for a typo" />}
      </td>
      <td className="px-2 py-1 whitespace-nowrap">{r.zip} <span className="text-slate-400">{r.country}</span></td>
      <td className="px-2 py-1">
        {locked ? <span className="text-slate-500">{(cands[r.pick] || {}).customer || r.note}</span>
          : <MatchSelect r={r} cands={cands} value={s.cid} onChange={(cid) => set({ cid, on: !!cid, replace: false })} />}
      </td>
      <td className="px-2 py-1 text-center">{m ? ZIP_ICON[m.zip] : ""}</td>
      <td className="px-2 py-1 text-center">{m?.days ?? ""}</td>
      <td className="px-2 py-1 font-mono text-slate-500">{c.tracking || ""}</td>
      <td className="px-2 py-1 text-center">
        {needsReplace && (
          <label className="inline-flex items-center gap-1 text-rose-700 font-semibold">
            <input type="checkbox" checked={s.replace} onChange={(e) => set({ replace: e.target.checked })}
                   data-testid={`emi-row-replace-${r.row}`} />Replace
          </label>)}
      </td>
      <td className="px-2 py-1 text-slate-500 max-w-[240px]">{r.note}</td>
    </tr>
  );
};

const Preview = ({ data, onDone }) => {
  const [sel, setSel] = useState(() => initialSel(data.rows));
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const rows = useMemo(() => data.rows.filter(r =>
    (filter === "all" || (filter === "replace" ? r.replace_needed : r.status === filter)) &&
    (!q.trim() || `${r.name} ${r.tracking} ${r.zip}`.toLowerCase().includes(q.trim().toLowerCase()))),
  [data.rows, filter, q]);
  const chosen = data.rows.filter(r => sel[r.row].on && sel[r.row].cid);
  const blocked = chosen.filter(r => {
    const t = (data.cands[sel[r.row].cid] || {}).tracking;
    return t && !isTemp(t) && t !== r.tracking && !sel[r.row].replace;
  });
  const set = (row) => (patch) => setSel(s => ({ ...s, [row]: { ...s[row], ...patch } }));
  const selectReview = () => setSel(s => {
    const n = { ...s };
    data.rows.forEach(r => { if (r.status === "review" && n[r.row].cid) n[r.row] = { ...n[r.row], on: true }; });
    return n;
  });
  const excel = async () => {
    try {
      const r = await axios.get(`${API}/order-register/emirates-tracking/preview/${data.id}/excel`,
        { headers: getAuthHeader(), responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = `Emirates_Tracking_Preview_${data.filename.replace(/\.[^.]+$/, "")}.xlsx`;
      a.click();
    } catch { toast.error("Excel download failed"); }
  };
  const apply = async () => {
    setBusy(true);
    try {
      const items = chosen.map(r => ({ row: r.row, cid: sel[r.row].cid, replace: sel[r.row].replace }));
      const r = await axios.post(`${API}/order-register/emirates-tracking/apply`, { preview_id: data.id, items },
        { headers: getAuthHeader() });
      setResult(r.data);
      toast.success(`Trackings applied to ${r.data.applied} parcels`);
      onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Apply failed"); } finally { setBusy(false); }
  };
  if (result) return (
    <div className="space-y-3" data-testid="emi-apply-result">
      <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm">
        <p className="font-semibold text-emerald-800">{result.applied} parcels updated</p>
        <p className="text-xs text-emerald-700">Order Register rows: {result.or_rows} · Database A rows: {result.dba_rows} · weights filled: {result.weights}</p>
      </div>
      {result.skipped?.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs" data-testid="emi-apply-skipped">
          <p className="font-semibold text-amber-800 mb-1">{result.skipped.length} skipped</p>
          {result.skipped.slice(0, 30).map((s, i) => <p key={i}>{s.tracking} · {s.name} — {s.reason}</p>)}
        </div>)}
      <Recent />
    </div>
  );
  const chip = (id, label, n) => (
    <button key={id} type="button" onClick={() => setFilter(id)} data-testid={`emi-filter-${id}`}
            className={`px-2.5 py-1 rounded-full border text-[11px] font-semibold transition-colors ${filter === id
              ? "bg-slate-800 text-white border-slate-800" : `${STATUS[id]?.cls || "bg-white text-slate-700 border-slate-300"} hover:opacity-80`}`}>
      {label} <span className="opacity-70">{n}</span>
    </button>);
  return (
    <div className="space-y-3" data-testid="emi-preview">
      <div className="flex flex-wrap gap-1.5">
        {chip("all", "All", data.rows.length)}
        {Object.keys(STATUS).map(k => chip(k, STATUS[k].label, data.counts[k] || 0))}
        {chip("replace", "Needs replace tick", data.counts.replace || 0)}
      </div>
      <div className="flex flex-wrap gap-1 text-[10px]" data-testid="emi-sheets">
        {data.sheets.map(s => (
          <span key={s.name} className={`border rounded px-1.5 py-0.5 ${s.issue ? "border-rose-300 bg-rose-50 text-rose-700" : "bg-white text-slate-600"}`}
                title={s.issue || (s.header ? "Header row found" : "No header — columns detected from content")}>
            {s.name} → {s.date || "?"} · {s.rows}{!s.header && s.rows ? " · no header" : ""}{s.duplicates ? ` · ${s.duplicates} dup skipped` : ""}
          </span>))}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-56">
          <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name / tracking / zip"
                 className="h-7 pl-7 text-xs" data-testid="emi-search" />
        </div>
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => {
          if (!data.rows.some(r => r.status === "review" && r.pick)) toast.info("No Review rows to tick — everything is already set, conflicting or not found");
          selectReview();
        }} data-testid="emi-select-review">
          Tick all Review matches
        </Button>
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={excel} data-testid="emi-download-excel">
          <Download className="w-3 h-3 mr-1" />Excel (preview + fuzzy + &gt;15 days)
        </Button>
        <Button size="sm" className="h-7 text-xs ml-auto bg-emerald-600 hover:bg-emerald-700 text-white"
                disabled={busy || !chosen.length || blocked.length > 0} onClick={apply} data-testid="emi-apply-btn"
                title={blocked.length ? `${blocked.length} ticked row(s) would replace a real tracking — tick Replace or untick them` : ""}>
          {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Check className="w-3 h-3 mr-1" />}
          Apply {chosen.length} {blocked.length > 0 && `(${blocked.length} need Replace)`}
        </Button>
      </div>
      <div className="overflow-auto max-h-[55vh] border rounded-lg">
        <table className="w-full text-left" data-testid="emi-preview-table">
          <thead className="bg-slate-50 sticky top-0 text-[10px] uppercase text-slate-500">
            <tr>{["", "Status", "Dispatch", "Name (file)", "Tracking", "Zip", "Match (order)", "Zip", "Days", "Current", "", "Note"]
              .map((h, i) => <th key={i} className="px-2 py-1.5 font-semibold">{h}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map(r => <Row key={r.row} r={r} cands={data.cands} s={sel[r.row]} set={set(r.row)} />)}
          </tbody>
        </table>
        {!rows.length && <p className="text-center text-xs text-slate-400 py-6">No rows in this filter</p>}
      </div>
    </div>
  );
};

export const EmiratesTrackingUpdate = ({ testId, onApplied, compact }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);
  const [tick, setTick] = useState(0);
  const ref = useRef(null);
  const applied = () => { setTick(t => t + 1); onApplied?.(); };
  const upload = async (f) => {
    if (!f) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await axios.post(`${API}/order-register/emirates-tracking/preview`, fd, { headers: getAuthHeader() });
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not read the file"); } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => { setData(null); setOpen(true); }}
              className="h-8 text-[11px] font-semibold border-sky-400 text-sky-700 hover:bg-sky-50"
              title="Upload the Emirates tracking workbook — trackings are matched by customer name, zip and order date"
              data-testid={`emi-tracking-btn-${testId}`}>
        <PlaneTakeoff className="w-3.5 h-3.5 sm:mr-1" />{!compact && <span className="hidden sm:inline">Emirates Tracking</span>}
      </Button>
      <AwaitingTrackingBadge testId={testId} refreshKey={tick} onUpload={() => { setData(null); setOpen(true); }} />
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-[96vw] w-[1280px] max-h-[94vh] overflow-y-auto" data-testid="emi-tracking-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><PlaneTakeoff className="w-4 h-4 text-sky-600" />Emirates Tracking Update</DialogTitle>
            <DialogDescription className="text-xs">
              Updates Order Register + Database A together. Matched by customer name, zip and order date
              (0–7 days Auto, 8–15 days Review). Only blank / temporary EMI trackings are filled unless you tick Replace.
            </DialogDescription>
          </DialogHeader>
          {data ? <Preview data={data} onDone={applied} /> : (
            <div>
              <div onClick={() => ref.current?.click()}
                   onDragOver={(e) => e.preventDefault()}
                   onDrop={(e) => { e.preventDefault(); upload(e.dataTransfer.files?.[0]); }}
                   className="border-2 border-dashed border-sky-300 rounded-xl p-8 text-center cursor-pointer hover:bg-sky-50 transition-colors"
                   data-testid="emi-dropzone">
                {busy ? <Loader2 className="w-6 h-6 mx-auto animate-spin text-sky-600" /> : <Upload className="w-6 h-6 mx-auto text-sky-600" />}
                <p className="text-sm font-semibold text-slate-700 mt-2">{busy ? "Reading every sheet and matching…" : "Drop the Emirates tracking workbook or click to choose"}</p>
                <p className="text-[11px] text-slate-500 mt-1">One sheet per dispatch date (e.g. 21-sep, 7 OCT). Header row optional — columns are detected from the content.</p>
              </div>
              <input ref={ref} type="file" accept=".xlsx,.xlsm" className="hidden"
                     onChange={(e) => upload(e.target.files?.[0])} data-testid="emi-file-input" />
              <Recent onUndone={applied} />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
